<template>
  <v-container>
    <h1 class="headline">List of Activities</h1>
    <v-data-table
      :headers="headers"
      :items="items"
      :loading="items.length == 0"
      item-key="event_id"
      select-all
      v-model="selected"
      class="elevation-3 mt-2"
    >
      <v-progress-linear slot="progress" indeterminate></v-progress-linear>
      <template slot="items" slot-scope="props">
        <td>
          <v-checkbox
            primary
            hide-details
            v-model="props.selected"
          ></v-checkbox>
        </td>
        <td>{{ props.item.name }}</td>
        <td>{{ props.item.description }}</td>
        <td>{{ props.item.start_date }}</td>
        <td>{{ props.item.end_date }}</td>
      </template>
      <template slot="no-data">
        Nothing to display here :(
      </template>
    </v-data-table>

    <!-- ACTION MODAL -->
    <v-dialog v-model="eventDialog" persistent max-width="500px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ this.checkIfAdd ? 'Add' : 'Edit' }} an Event</span>
        </v-card-title>
        <v-card-text>
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs12>
                <v-text-field 
                  label="Event Name" 
                  required 
                  v-model.trim="eventName"
                  :error-messages="eventNameErrors"
                  @input="$v.eventName.$touch()"
                  @blur="$v.eventName.$touch()"
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-text-field label="Description" multi-line v-model="description"></v-text-field>
              </v-flex>
              <v-flex xs12 sm6>
                <v-menu
                  lazy
                  v-model="startDateMenu"
                  transition="scale-transition"
                  :nudge-right="40"
                  max-width="290px"
                  min-width="290px"
                >
                  <v-text-field
                    slot="activator"
                    label="Start Date"
                    v-model="startDate"
                    prepend-icon="event"
                    :error-messages="startDateErrors"
                    @input="$v.startDate.$touch()"
                    @blur="$v.startDate.$touch()"
                    readonly
                    required
                  ></v-text-field>
                  <v-date-picker v-model="startDate" no-title scrollable actions>
                    <template slot-scope="{ save, cancel }">
                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn flat color="gray" @click="cancel">Cancel</v-btn>
                        <v-btn flat color="primary" @click="save">OK</v-btn>
                      </v-card-actions>
                    </template>
                  </v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex xs12 sm6>
                <v-menu
                  lazy
                  v-model="endDateMenu"
                  transition="scale-transition"
                  :nudge-right="40"
                  max-width="290px"
                  min-width="290px"
                >
                  <v-text-field
                    slot="activator"
                    label="End Date"
                    v-model="endDate"
                    prepend-icon="event"
                    :error-messages="endDateErrors"
                    @input="$v.endDate.$touch()"
                    @blur="$v.endDate.$touch()"
                    readonly
                    required
                  ></v-text-field>
                  <v-date-picker v-model="endDate" :allowed-dates="allowedDates" no-title scrollable actions>
                    <template slot-scope="{ save, cancel }">
                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn flat color="gray" @click="cancel">Cancel</v-btn>
                        <v-btn flat color="primary" @click="save">OK</v-btn>
                      </v-card-actions>
                    </template>
                  </v-date-picker>
                </v-menu>
              </v-flex>
              <small>*indicates required field</small>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="gray" flat @click.native="clearFormField">Close</v-btn>
          <v-btn color="blue darken-1" flat @click="addCalendarEvent" v-if="checkIfAdd">Save</v-btn>
          <v-btn color="blue darken-1" flat @click="editCalendarEvent" v-else>Edit</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DELETE MODAL -->
    <v-layout row justify-center>
      <v-dialog v-model="deleteModal" persistent max-width="290">
        <v-card>
          <v-card-title class="headline">Warning!</v-card-title>
          <v-card-text>You are about to delete {{ this.selected.length }} event{{ this.selected.length > 0 ? 's' : '' }}. Are you sure?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="gray" flat @click.native="deleteModal = false">Cancel</v-btn>
            <v-btn color="red darken-1" flat @click.native="deleteCalendarEvent">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-layout>

    <!-- FLOATING BUTTON -->
    <v-speed-dial
      v-model="fab"
      :bottom="bottom"
      :right="right"
      :direction="direction"
      :transition="transition"
      fixed
      :hover="true"
    >
      <v-btn
        slot="activator"
        fab
        color="red darken-1"
        dark
        hover
        v-model="fab"
      >
        <v-icon>more_vert</v-icon>
        <v-icon>close</v-icon>
      </v-btn>
      <v-tooltip left lazy>
        <v-btn
          slot="activator"
          small
          fab
          dark
          color="green"
          @click="setEventAction(true)"
        >
          <v-icon>add</v-icon>
        </v-btn>
        <span>Add</span>
      </v-tooltip>
      <v-tooltip left lazy>
        <v-btn
          slot="activator"
          small
          fab
          dark
          color="amber"
          v-if="editSelected"
          @click="setEventAction(false)"
        >
          <v-icon>edit</v-icon>
        </v-btn>
        <span>Edit</span>
      </v-tooltip>
      <v-tooltip left lazy>
        <v-btn
          slot="activator"
          small
          fab
          dark
          color="red"
          @click="deleteModal = true"
          v-if="deleteSelected"
        >
          <v-icon>delete</v-icon>
        </v-btn>
        <span>Delete</span>
      </v-tooltip>
    </v-speed-dial>
  </v-container>
</template>

<script>
import VueNotifications from 'vue-notifications'
import axios from 'axios'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
export default {
  mixins: [validationMixin],
  validations: {
    eventName: {
      required
    },
    startDate: {
      required
    },
    endDate: {
      required
    }
  },
  data(){
    return {
      fab: false,
      bottom: true,
      right: true,
      transition: 'slide-y-reverse-transition',
      direction: 'top',
      selected: [],
      headers: [
        { text: 'Name', value: 'name', align: 'left' },
        { text: 'Description', value: 'description', align: 'left' },
        { text: 'Start Date', value: 'start_date', align: 'left' },
        { text: 'End Date', value: 'end_date', align: 'left' }
      ],
      eventDialog: false,
      startDateMenu: false,
      startDate: null,
      startDatePicker: false,
      endDateMenu: false,
      endDate: null,
      endDatePicker: false,
      eventName: '',
      description: '',
      deleteModal: false,
      checkIfAdd: null,
      eventId: null
    }
  },
  methods: {
    async addCalendarEvent(){
      let { startDate, endDate, description, eventName } = this
      this.$v.$touch()

      if(!this.$v.$error){
        try{
          this.$Progress.start()
          let response = await axios.post('/api/calendar', {
              name: eventName,
              description: description,
              start_date: startDate,
              end_date: endDate
            }, { headers: { 'Authorization': `Bearer ${this.$store.getters.GET_TOKEN}` } })
          this.$store.dispatch('admin/CALENDAR_EVENTS')
          this.clearFormField()
          this.eventDialog = false
          this.showSuccessMsg({ title: 'Success!', message: 'Calendar event is successfully added!' })
          this.$Progress.finish()
        } catch(e){
          this.clearFormField()
          this.eventDialog = false
          console.log(e)
          this.showErrorMsg({ title: e.response.statusText, message: e.response.data.error.message })
          this.$Progress.fail()
        }
      }
    },
    async editCalendarEvent(){
      let { startDate, endDate, description, eventName, eventId } = this
      this.$v.$touch()
      
      if(!this.$v.$error){
        try{
          this.$Progress.start()
          let result = await axios.put(`/api/calendar/${ eventId }`, {
            name: eventName,
            description: description,
            start_date: startDate,
            end_date: endDate
          }, { headers: { 'Authorization': `Bearer ${this.$store.getters.GET_TOKEN}` } })
          this.$store.dispatch('admin/CALENDAR_EVENTS')
          this.clearFormField()
          this.eventDialog = false
          this.showSuccessMsg({ title: 'Success!', message: 'Calendar event is successfully updated!' })
          this.selected = []
          this.$Progress.finish()
        }catch(e){
          this.clearFormField()
          this.eventDialog = false
          console.log(e)
          this.showErrorMsg({ title: e.response.statusText, message: e.response.data.error.join(' ') })
          this.$Progress.fail()
        }
        
      }
    },
    async deleteCalendarEvent(){
      let events = this.selected.map(event => {
        return event.event_id
      })
      
      try{
        this.$Progress.start()
        let response = await axios.delete('/api/calendar', {
          params: {
            ids: events
          },
          headers: { 
            'Authorization': `Bearer ${this.$store.getters.GET_TOKEN}`
          }})
        this.$store.dispatch('admin/CALENDAR_EVENTS')
        this.clearFormField()
        this.deleteModal = false
        this.showSuccessMsg({ title: 'Success!', message: 'Calendar event is successfully deleted!' })
        this.selected = []
        this.$Progress.finish()
      }catch(e){
        this.$store.dispatch('admin/CALENDAR_EVENTS')
        console.log(e)
        this.showErrorMsg({ title: e.response.statusText, message: e.response.data.error.join(' ') })
        this.$Progress.fail()
      }
    },
    setEventAction(action){
      // If action is true then it is add, else it is false
      this.checkIfAdd = action ? true : false
      // If edit is clicked
      if(!action) {
        const { description, end_date, event_id, name, start_date } = this.selected[0]
        this.eventName = name
        this.description = description
        this.startDate = start_date
        this.endDate = end_date
        this.eventId = event_id
      }

      this.eventDialog = true
    },
    clearFormField(){
      this.eventDialog = false
      this.startDate = null
      this.endDate = null
      this.eventName = ''
      this.description = ''
    },
    allowedDates(val){
      if(this.startDate){
        return this.startDate <= val
      }
    }
  },
  created(){
    this.$store.dispatch('admin/CALENDAR_EVENTS')
  },
  computed: {
    editSelected(){
      return this.selected.length == 1 ? true : false
    },
    deleteSelected(){
      return this.selected.length > 0 ? true : false
    },
    items(){
      return this.$store.getters['admin/GET_CALENDAR_EVENTS']
    },
    eventNameErrors(){
      const errors = []
      if(!this.$v.eventName.$dirty) return errors
      !this.$v.eventName.required && errors.push('Event Name is required')
      return errors
    },
    startDateErrors(){
      const errors = []
      if(!this.$v.startDate.$dirty) return errors
      !this.$v.startDate.required && errors.push('Start Date is required')
      return errors
    },
    endDateErrors(){
      const errors = []
      if(!this.$v.endDate.$dirty) return errors
      !this.$v.endDate.required && errors.push('End Date is required')
      return errors
    }
  },
  notifications: {
    showSuccessMsg: { type: VueNotifications.types.success },
    showErrorMsg: { type: VueNotifications.types.error }
  }
}
</script>

<style>
.swal-title,
.swal-text,
.swal-button{
  font-family: 'Roboto',sans-serif;
}

.swal-button{
  background-color: #43a047;
}
</style>



// WEBPACK FOOTER //
// src/components/Admin/Calendar.vue